import React, { useState } from 'react';
import {
	Button,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	Stack,
	Typography,
} from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import { showToast } from '@/features/toast/toastSlice';
import { closeDialog } from '@/features/dialog/dialogSlice';
import { SCOPES } from '@/features/config/scopes';
import { ShareField } from '@/components/dialogs/ShareDialog/ShareField';
import { AdminLedgerPicker } from '../shared/pickers/AdminLedgerPicker';
import { AdminBrokerGroupPicker } from '../shared/pickers/AdminBrokerGroupPicker';
import { useAdminConfirm } from '../shared/AdminConfirmProvider';
import { useCopyFormFieldConfigsMutation } from './api/formFieldConfigsApi';

export const COPY_FORM_FIELD_CONFIGS_DIALOG = 'copyFormFieldConfigs';

/**
 * Copies the ticked form field configs into any ledgers and broker groups, in any ledger,
 * after the admin confirms. Each target's config for those fields is replaced with a copy of
 * the current settings, so copying again after an edit overwrites earlier copies; later edits
 * don't follow by themselves. Opened with openDialog({ dialogName:
 * COPY_FORM_FIELD_CONFIGS_DIALOG, data: { docIds } }); mount it only while open, so each
 * opening starts empty.
 */
export const CopyFormFieldConfigsDialog = () => {
	const dispatch = useDispatch();
	const confirm = useAdminConfirm();
	const { docIds } = useSelector((state) => state.dialog.data[COPY_FORM_FIELD_CONFIGS_DIALOG]);
	const onClose = () => dispatch(closeDialog(COPY_FORM_FIELD_CONFIGS_DIALOG));
	const [ledgers, setLedgers] = useState([]);
	const [brokerGroupIds, setBrokerGroupIds] = useState([]);
	const [copyFormFieldConfigs, { isLoading }] = useCopyFormFieldConfigsMutation();

	const targets = [
		...ledgers.map((scopeId) => ({ scope: SCOPES.LEDGER, scopeId })),
		...brokerGroupIds.map((scopeId) => ({ scope: SCOPES.BROKER_GROUP, scopeId })),
	];

	const handleCopy = async () => {
		const confirmed = await confirm({
			title: `Copy ${docIds.length} field${docIds.length === 1 ? '' : 's'}?`,
			content:
				"This replaces each chosen ledger's and broker group's settings for these fields, including earlier copies. A broker group's own config still wins over its ledger's.",
			confirmText: 'Copy',
		});
		if (!confirmed) return;

		const result = await copyFormFieldConfigs({ docIds, targets });
		if (result.error) {
			dispatch(
				showToast({ message: "Couldn't copy the config. Please try again.", error: true }),
			);
			return;
		}
		dispatch(
			showToast({
				message: 'Copied the config',
				success: true,
				duration: 4000,
				autohide: true,
			}),
		);
		onClose();
	};

	return (
		<Dialog
			open
			onClose={onClose}
			slotProps={{ paper: { sx: { minWidth: { xs: '100%', sm: '70%', md: '50%' } } } }}
		>
			<DialogTitle>{`Copy ${docIds.length} config setting${docIds.length === 1 ? '' : 's'}`}</DialogTitle>
			<DialogContent sx={{ paddingBottom: '2em' }}>
				<Stack spacing={2}>
					<Typography variant='body2'>
						Copies these settings into each chosen ledger and broker group, replacing
						their settings for these fields.
					</Typography>
					<ShareField label='Ledgers'>
						<AdminLedgerPicker value={ledgers} onChange={setLedgers} showCheckbox />
					</ShareField>
					<ShareField label='Broker groups'>
						<AdminBrokerGroupPicker
							value={brokerGroupIds}
							onChange={setBrokerGroupIds}
							showCheckbox
						/>
					</ShareField>
				</Stack>
			</DialogContent>
			<DialogActions>
				<Button onClick={onClose}>Cancel</Button>
				<Button
					variant='contained'
					onClick={handleCopy}
					disabled={!targets.length || isLoading}
				>
					Copy
				</Button>
			</DialogActions>
		</Dialog>
	);
};

CopyFormFieldConfigsDialog.displayName = 'CopyFormFieldConfigsDialog';
